import { 
  FlameIcon,
  UsersIcon,
  SparklesIcon,
  TrendingUpIcon,
  ArrowRightIcon
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Button from '@/components/form/Button';

const features = [
  {
    icon: <FlameIcon className="h-6 w-6 text-orange-600" />,
    title: "FIRESNAPS",
    description: "Viral questions and hot topics curated by AI, automatically turned into engaging SNAPs for community voting. These system-generated SNAPs capture trending discussions and current events, letting you participate in what's moving the world right now."
  },
  {
    icon: <UsersIcon className="h-6 w-6 text-primary-600" />,
    title: "Community Engagement",
    description: "Follow friends, share SNAPs, and invite others to private discussions. Build your network and make decisions together with people who matter to you."
  },
  {
    icon: <SparklesIcon className="h-6 w-6 text-primary-600" />,
    title: "100% Free",
    description: "All features are completely free! No premium tiers, no hidden costs, no ads. We believe in making collective decision-making accessible to everyone."
  }
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function EmergingContent() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="py-16 bg-gradient-to-b from-white to-primary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="lg:text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Emerging Content</h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Stay Connected with What Matters
          </p>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
            Discover trending topics, engage with your community, and be part of global conversations.
          </p>
        </motion.div>

        <motion.div 
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mt-10"
        >
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                variants={item}
                className="pt-6"
              >
                <div className="flow-root bg-white rounded-lg px-6 pb-8 h-full hover:shadow-lg transition-shadow duration-300">
                  <div className="-mt-6">
                    <div>
                      <span className={`inline-flex items-center justify-center p-3 rounded-md shadow-lg ${
                        feature.title === 'FIRESNAPS' 
                          ? 'bg-gradient-to-br from-orange-100 to-orange-200'
                          : 'bg-gradient-to-br from-primary-100 to-primary-200'
                      }`}>
                        {feature.icon}
                      </span>
                    </div>
                    <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight flex items-center gap-2">
                      {feature.title}
                      {feature.title === 'FIRESNAPS' && (
                        <TrendingUpIcon className="h-4 w-4 text-orange-500 animate-pulse" />
                      )}
                    </h3>
                    <p className="mt-5 text-base text-gray-500">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Button
            onClick={handleGetStarted}
            variant="primary"
            className="px-8 py-3 md:py-4 md:px-10 text-base md:text-lg"
          >
            Show SNAPs
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
}